import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft, ArrowRight, ArrowUpLeft, ArrowUpRight, ArrowDownLeft, ArrowDownRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface GreenAttackZonesChartProps {
  selectedLevel: string | null;
  traineeUserId?: string;
}

interface Zone2Data {
  level: string;
  testId: string;
  testName: string;
  left: number;
  right: number;
  total: number;
  averageScore: number;
}

interface Zone4Data {
  level: string;
  testId: string;
  testName: string;
  bottomLeft: number;
  topLeft: number;
  bottomRight: number;
  topRight: number;
  total: number;
  averageScore: number;
}

const GreenAttackZonesChart: React.FC<GreenAttackZonesChartProps> = ({ selectedLevel, traineeUserId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [zone2Data, setZone2Data] = useState<Zone2Data[]>([]);
  const [zone4Data, setZone4Data] = useState<Zone4Data[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setLoading(false);
          return;
        }

        // Utiliser traineeUserId si fourni (mode coach), sinon utiliser l'utilisateur connecté
        const userId = traineeUserId || session.user.id;

        // Fetch all green attack results from individual_scores
        const { data: results, error } = await supabase
          .from('individual_scores')
          .select('*')
          .eq('user_id', userId)
          .in('test_id', ['iron-green-attack', 'iron-green-attack-100'])
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching green attack results:', error);
          setLoading(false);
          return;
        }

        if (!results || results.length === 0) {
          setLoading(false);
          return;
        }

        // Separate 2-zone and 4-zone exercises
        const zone2Results: Record<string, { testId: string; testName: string; left: number; right: number; total: number; totalScore: number }> = {};
        const zone4Results: Record<string, { testId: string; testName: string; bottomLeft: number; topLeft: number; bottomRight: number; topRight: number; total: number; totalScore: number }> = {};

        results.forEach((result: any) => {
          const level = result.level || '1';
          const testId = result.test_id || '';
          
          // Get test name from test_id
          let testName = testId;
          if (testId === 'iron-green-attack-100') {
            testName = t('training:interface.scoring.greenAttack.zonesTitle100');
          } else if (testId === 'iron-green-attack') {
            testName = t('training:interface.scoring.greenAttack.zonesTitle120');
          }
          
          const key = `${level}-${testId}`;
          const zone = result.tendency;

          // Only process results with zone information
          if (!zone) return;

          // Level 1 uses 4 zones, Levels 2 and 3 use 2 zones
          if (level === '1') {
            if (!zone4Results[key]) {
              zone4Results[key] = { testId, testName, bottomLeft: 0, topLeft: 0, bottomRight: 0, topRight: 0, total: 0, totalScore: 0 };
            }

            if (zone === 'bottom-left') zone4Results[key].bottomLeft++;
            else if (zone === 'top-left') zone4Results[key].topLeft++;
            else if (zone === 'bottom-right') zone4Results[key].bottomRight++;
            else if (zone === 'top-right') zone4Results[key].topRight++;

            zone4Results[key].total++;
            zone4Results[key].totalScore += result.score || 0;
          } else {
            if (!zone2Results[key]) {
              zone2Results[key] = { testId, testName, left: 0, right: 0, total: 0, totalScore: 0 };
            }

            if (zone === 'left') zone2Results[key].left++;
            else if (zone === 'right') zone2Results[key].right++;

            zone2Results[key].total++;
            zone2Results[key].totalScore += result.score || 0;
          }
        });

        // Convert to array format
        const zone2Array = Object.entries(zone2Results).map(([key, data]) => {
          const level = key.split('-')[0];
          return {
            level: `${t('common.level')} ${level}`,
            testId: data.testId,
            testName: data.testName,
            left: data.left,
            right: data.right,
            total: data.total,
            averageScore: data.total > 0 ? Math.round((data.totalScore / data.total) * 10) / 10 : 0
          };
        });

        const zone4Array = Object.entries(zone4Results).map(([key, data]) => {
          const level = key.split('-')[0];
          return {
            level: `${t('common.level')} ${level}`,
            testId: data.testId,
            testName: data.testName,
            bottomLeft: data.bottomLeft,
            topLeft: data.topLeft,
            bottomRight: data.bottomRight,
            topRight: data.topRight,
            total: data.total,
            averageScore: data.total > 0 ? Math.round((data.totalScore / data.total) * 10) / 10 : 0
          };
        });

        setZone2Data(zone2Array);
        setZone4Data(zone4Array);
      } catch (error) {
        console.error('Error fetching green attack data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t, traineeUserId]);

  // Filter by selected level if applicable
  const filteredZone2Data = selectedLevel 
    ? zone2Data.filter(d => d.level === `${t('common.level')} ${selectedLevel}`)
    : zone2Data;

  const filteredZone4Data = selectedLevel 
    ? zone4Data.filter(d => d.level === `${t('common.level')} ${selectedLevel}`)
    : zone4Data;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="flex justify-center my-4">
            <Loader2 className="h-6 w-6 text-golf-600 animate-spin" />
          </div>
          <p className="text-muted-foreground">{t('training:charts.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (filteredZone2Data.length === 0 && filteredZone4Data.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          <p>Aucune donnée de zones de green disponible</p>
          <p className="text-sm mt-2">Les zones de green sont enregistrées lors des exercices d'attaque de green 100m et 120m</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* 4-zone exercises (Level 1) */}
      {filteredZone4Data.length > 0 && filteredZone4Data.map((exerciseData) => (
        <Card key={`${exerciseData.level}-${exerciseData.testId}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              🎯 {exerciseData.testName}
              <span className="text-sm font-normal text-muted-foreground">
                ({exerciseData.level})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              {/* 4-zone grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Top-left */}
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowUpLeft className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">
                      {t('training:interface.scoring.greenAttack.topLeft')}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{exerciseData.topLeft}</p>
                  <p className="text-xs text-blue-600">
                    {exerciseData.total > 0 ? Math.round((exerciseData.topLeft / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
                
                {/* Top-right */}
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowUpRight className="h-4 w-4 text-green-600" />
                    <span className="text-xs font-medium text-green-700">
                      {t('training:interface.scoring.greenAttack.topRight')}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{exerciseData.topRight}</p>
                  <p className="text-xs text-green-600">
                    {exerciseData.total > 0 ? Math.round((exerciseData.topRight / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
                
                {/* Bottom-left */}
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowDownLeft className="h-4 w-4 text-purple-600" />
                    <span className="text-xs font-medium text-purple-700">
                      {t('training:interface.scoring.greenAttack.bottomLeft')}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-purple-600">{exerciseData.bottomLeft}</p>
                  <p className="text-xs text-purple-600">
                    {exerciseData.total > 0 ? Math.round((exerciseData.bottomLeft / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
                
                {/* Bottom-right */}
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowDownRight className="h-4 w-4 text-orange-600" />
                    <span className="text-xs font-medium text-orange-700">
                      {t('training:interface.scoring.greenAttack.bottomRight')}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-orange-600">{exerciseData.bottomRight}</p>
                  <p className="text-xs text-orange-600">
                    {exerciseData.total > 0 ? Math.round((exerciseData.bottomRight / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
              </div>
              
              {/* Aggregated Top/Bottom View */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-sky-50 p-3 rounded-lg border border-sky-200">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-sm font-medium text-sky-700">🔼 Haut</span>
                  </div>
                  <p className="text-2xl font-bold text-sky-600 text-center">
                    {exerciseData.topLeft + exerciseData.topRight}
                  </p>
                  <p className="text-xs text-sky-600 text-center">
                    {exerciseData.total > 0 ? Math.round(((exerciseData.topLeft + exerciseData.topRight) / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
                
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-sm font-medium text-amber-700">🔽 Bas</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-600 text-center">
                    {exerciseData.bottomLeft + exerciseData.bottomRight}
                  </p>
                  <p className="text-xs text-amber-600 text-center">
                    {exerciseData.total > 0 ? Math.round(((exerciseData.bottomLeft + exerciseData.bottomRight) / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="bg-golf-50 p-3 rounded-lg border border-golf-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('common.total')}:</span>
                    <span className="font-semibold ml-2">{exerciseData.total} {t('training:charts.shots')}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('common.average')}:</span>
                    <span className="font-semibold ml-2">{exerciseData.averageScore} pts</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* 2-zone exercises (Levels 2 & 3) */}
      {filteredZone2Data.length > 0 && filteredZone2Data.map((exerciseData) => (
        <Card key={`${exerciseData.level}-${exerciseData.testId}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              🎯 {exerciseData.testName}
              <span className="text-sm font-normal text-muted-foreground">
                ({exerciseData.level})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              {/* Zone Distribution */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowLeft className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">
                      {t('training:interface.scoring.greenAttack2Zone.left')}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{exerciseData.left}</p>
                  <p className="text-xs text-blue-600">
                    {exerciseData.total > 0 ? Math.round((exerciseData.left / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowRight className="h-4 w-4 text-green-600" />
                    <span className="text-xs font-medium text-green-700">
                      {t('training:interface.scoring.greenAttack2Zone.right')}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{exerciseData.right}</p>
                  <p className="text-xs text-green-600">
                    {exerciseData.total > 0 ? Math.round((exerciseData.right / exerciseData.total) * 100) : 0}%
                  </p>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="bg-golf-50 p-3 rounded-lg border border-golf-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('common.total')}:</span>
                    <span className="font-semibold ml-2">{exerciseData.total} {t('training:charts.shots')}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('common.average')}:</span>
                    <span className="font-semibold ml-2">{exerciseData.averageScore} pts</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default GreenAttackZonesChart;
